import { SearchField } from "@amzn/meridian";
import Table, {
  TableActionBar,
  TableCell,
  TableRow,
} from "@amzn/meridian/table";
import { TableSortDirection } from "@amzn/meridian/table/table";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PaginationWithDropdown from "src/components/shared/paginationWithDropdown/paginationWithDropdown";
import TableCellRenderer from "src/components/shared/tableCellRenderer/tableCellRenderer";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { TableColumn } from "src/types/utilTypes";
import {
  filterSortAndPaginateTableData,
  SortStrategy,
} from "src/utils/tableUtils";
import {
  setPaginationState,
  setSearchField,
  setSortState,
  visitConversation,
} from "../store/adminSlice";
import { ConversationSummary } from "../types";

const ConversationsTable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<ConversationSummary[]>([]);
  const {
    conversations,
    paginationState,
    sortState,
    searchField,
    visitedConversations,
  } = useAppSelector((state) => state.adminSlice);

  const { sortColumn, sortDirection } = sortState;
  const { currentPage, perPage, totalPages } = paginationState;

  const onSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: keyof ConversationSummary;
    sortDirection: TableSortDirection;
  }) => {
    dispatch(setSortState({ sortDirection }));
    dispatch(setSortState({ sortColumn }));
  };

  useEffect(() => {
    const { totalPages, paginatedTableData } = filterSortAndPaginateTableData({
      tableData: conversations,
      searchField,
      currentPage,
      sortColumn,
      sortDirection,
      sortStrategy: SortStrategy.DATE,
      pageSize: perPage,
    });
    setTableData(paginatedTableData);
    dispatch(setPaginationState({ totalPages }));
  }, [
    conversations,
    sortColumn,
    sortDirection,
    searchField,
    currentPage,
    perPage,
  ]);

  const COLUMNS: TableColumn<ConversationSummary>[] = [
    { field: "id", label: "Conversation ID" },
    { field: "lastMessageTime", label: "Last Message Time", sortable: true },
    { field: "userAlias", label: "User", sortable: true },
    { field: "turnCount", label: "Turn Count", sortable: true },
    { field: "favoriteCount", label: "Favorite Count", sortable: true },
    { field: "voteUp", label: "Vote Up", sortable: true },
    { field: "voteDown", label: "Vote Down", sortable: true },
    {
      field: "saigeQuestionCount",
      label: "Saige Question Count",
      sortable: true,
    },
    {
      field: "saigeNoAnswerCount",
      label: "Saige No Answer Count",
      sortable: true,
    },
    { field: "exceptionCount", label: "Exception Count", sortable: true },
    { field: "durationInMinutes", label: "Duration (mins)", sortable: true },
  ];

  const onCoversationClick = (conversationId: string) => {
    dispatch(visitConversation(conversationId));
    navigate(`/admin/conversations/${conversationId}`);
  };

  const getRowColor = (conversationId: string) =>
    visitedConversations.includes(conversationId) ? "secondary" : "primary";

  return (
    <div className="conversations-table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        layout="auto"
        stickyHeaderRow
      >
        <TableActionBar>
          <SearchField
            value={searchField}
            onChange={(value) => dispatch(setSearchField(value))}
            placeholder="Search for..."
            onSubmit={() => {}}
          />
        </TableActionBar>
        <TableRow>
          {COLUMNS.map(({ field, label, sortable }) => (
            <TableCell key={field} sortColumn={sortable ? field : null}>
              {label}
            </TableCell>
          ))}
        </TableRow>
        {tableData.map((row) => (
          <TableRow
            key={row.id}
            highlightOnHover
            onClick={() => onCoversationClick(row.id)}
            backgroundColor={getRowColor(row.id)}
          >
            {COLUMNS.map(({ field }) => (
              <TableCellRenderer
                textColor={getRowColor(row.id)}
                data={row[field]}
                key={field}
                sortColumn={sortColumn}
              />
            ))}
          </TableRow>
        ))}
      </Table>
      <PaginationWithDropdown
        totalResults={conversations.length}
        totalPages={totalPages}
        currentPage={currentPage}
        perPage={perPage}
        setPerPage={(value) => dispatch(setPaginationState({ perPage: value }))}
        setCurrentPage={(value) =>
          dispatch(setPaginationState({ currentPage: value }))
        }
      />
    </div>
  );
};

export default ConversationsTable;
